import { Box, TextField } from "@mui/material";
import { useState, useEffect } from "react";

import styles from "../../../../assets/styles/componentStyles/templateLibraryStyles/whatsappTemplatePreview.module.scss";

import WrapWithHoverUI from "../utils/WrapWithHoverUI";
import VariableDialog from "../utils/VariableDialog";
import { renderVariableInText } from "../utils/renderVariableInText";
import { canUpdateTemplate, getTextCharacterLimit, getVariableRegex, isTemplateCategoryAuthentication, maxChars } from "../../channels/global/whatsapp/templates/utils";
import { useTemplateLibraryStore } from "../../../../store/templateLibraryStore";
import { useSearchParams } from "react-router-dom";
import { handleKeyDownForFormatting } from "../constant";

const WhatsAppMessageBodyPreview = (props: any) => {
    const { editable, displayBody, displayBodyVariableData, handleAddBodyVariableManually, removeVariableData, handleChangeText, handleChange, 
      handleRemoveField, textareaRef, handleFormatting } = props;

    const createTemplatePayload = useTemplateLibraryStore((state) => state.createTemplatePayload);
    const setCreateTemplatePayload = useTemplateLibraryStore((state) => state.setCreateTemplatePayload);
    const interactiveTypeList = useTemplateLibraryStore((state) => state.interactiveTypeList);
    const templateStatusDetails = useTemplateLibraryStore((state) => state.templateStatusDetails);
    const templateLibraryCategoryList = useTemplateLibraryStore((state) => state.templateLibraryCategoryList);
    const whatsappTemplateTypes = useTemplateLibraryStore((state) => state.whatsappTemplateTypes);

    const [hover, setHover] = useState(false);
    const [editingText, setEditingText] = useState(false);
    const [variableDialogOpen, setVariableDialogOpen] = useState(false);
    const [variableDialogAnchorEl, setVariableDialogAnchorEl] = useState<HTMLElement | null>(null);
    const [activeVariableKey, setActiveVariableKey] = useState<string | null>(null);

    // Constants
    const [searchParams] = useSearchParams();
    let draftId = searchParams.get('draft_id');

    const bodyVariableData = createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_variable_data ?? [];
    const bodyData = editable ? (createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_value ?? "") : (displayBody ?? "");
    const bodyVarsForDisplay = editable ? bodyVariableData : (displayBodyVariableData ?? []);
    const isEmptyText = !bodyData?.trim();
    const hasMissingSampleText = bodyVarsForDisplay?.some(
        (variable: any) => !variable?.whatsapp_template_dynamic_variable_sample_value || variable?.whatsapp_template_dynamic_variable_sample_value?.trim() === ""
    );


    // Dynamic Variable Functions
    const handleVariableAreaClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const variableElement = target.closest?.('[data-variable="true"]') || target.closest?.(`.${styles.whatsappPreview__variable}`);

        if (variableElement) {
            e.preventDefault();
            e.stopPropagation();
            setVariableDialogAnchorEl(variableElement as HTMLElement);
            setActiveVariableKey((variableElement as HTMLElement).getAttribute("data-variable-key"));
            setVariableDialogOpen(true);
        } else if (editable) {
            setEditingText(true);
        }
    };

    const handleVariableDialogClose = () => {
        setVariableDialogOpen(false);
        setVariableDialogAnchorEl(null);
        setActiveVariableKey(null);
    };

    // UseEffect
    useEffect(() => {
        if (!editable) {
            setVariableDialogOpen(false);
            setVariableDialogAnchorEl(null);
            setEditingText(false);
        }
    }, [editable]);

    useEffect(() => {
        if (editingText && textareaRef?.current) {
            const textTimeout = setTimeout(() => {
                const bodyTextareaElement = textareaRef?.current;
                if (!bodyTextareaElement) return;
                bodyTextareaElement?.focus();
                const bodyValueLength = (createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_value ?? '').length;
                bodyTextareaElement?.setSelectionRange?.(bodyValueLength, bodyValueLength);
            }, 0);
            return () => clearTimeout(textTimeout);
        }
    }, [editingText, textareaRef]);

    // Body Preview
    const wrapWithHoverUI = (content: any) => (
      <WrapWithHoverUI
        label="Body" 
        editable={editable} 
        onHoverChange={setHover}
        onMouseLeave={() => setEditingText(false)}
      >
        {content}
      </WrapWithHoverUI>
    );

    const renderBodyPreview = () => (
      <Box 
        className={`${styles.whatsappPreview__bodyText} ${editable && (hasMissingSampleText || isEmptyText) ? styles.whatsappPreview__textInputError : ""}`}
        dir="auto"
      >
        {bodyData?.trim()
          ? renderVariableInText(bodyData, bodyVarsForDisplay)
          : (
              <span
                className={styles.whatsappPreview__textContentPreview}
                dangerouslySetInnerHTML={{ __html: "Enter your body text here" }}
              />
            )}
      </Box>
    );

    if (!editable) {
        return renderBodyPreview();
    }

    const handleBodyTextDisabled = () => {
      return (
        (!canUpdateTemplate(templateStatusDetails?.whatsapp_template_metadata?.meta_whatsapp_template_status_name, createTemplatePayload, whatsappTemplateTypes, draftId))
        || isTemplateCategoryAuthentication(templateLibraryCategoryList, createTemplatePayload?.whatsapp_template_data?.whatsapp_template_category_id)
      )
    }

    return (
      <>
        {editable ? (
          wrapWithHoverUI(
                <div onClick={handleVariableAreaClick} className={styles.whatsappPreview__editableTextFieldWrapper}>
                  {editingText ? (
                    <TextField
                      multiline
                      fullWidth
                      inputProps={{ dir: 'auto' }}
                      InputProps={{ className: styles.whatsappPreview__textInputHoverContainer }}
                      // disabled={createTemplatePayload?.whatsapp_template_id ? true : false}
                      disabled={handleBodyTextDisabled()}
                      inputRef={textareaRef}
                      className={styles.whatsappPreview__bodyText}
                      value={createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body?.whatsapp_template_body_value ?? ""}
                      onChange={(event: any) => {
                        const value = event.target.value;
                        if (value.endsWith('{{')) {
                            handleAddBodyVariableManually(value, "body")
                        } else {
                            const transformedValue = value.replace(getVariableRegex(createTemplatePayload?.flag_named_parameter), '*');
                            let maximumBodyCharacterLimit = getTextCharacterLimit(createTemplatePayload, interactiveTypeList, "body text limit") ?? maxChars;
                            if (transformedValue.length <= maximumBodyCharacterLimit) {
                                let finalValues = removeVariableData(value, "body")
                                    handleChangeText(event, 'format');
                                    setCreateTemplatePayload({
                                        ...createTemplatePayload,
                                        whatsapp_template_data: {
                                            ...createTemplatePayload?.whatsapp_template_data,
                                            whatsapp_template_body: {
                                                ...createTemplatePayload?.whatsapp_template_data?.whatsapp_template_body,
                                                whatsapp_template_body_value: finalValues?.textAreaValue,
                                                whatsapp_template_body_variable_data: finalValues?.variableData
                                            },
                                        },
                                    });
                                }
                            }
                      }}
                      onKeyDown={(e) => handleKeyDownForFormatting(e, handleFormatting, "body")}
                      autoComplete='off'
                      minRows={3}
                      placeholder='Enter body text'
                    />
                  ) : (
                    renderBodyPreview()
                  )}
                </div>
              )
          ) : (
            renderBodyPreview()
          )
        }

        {editable && (
          <VariableDialog
            open={variableDialogOpen}
            anchorEl={variableDialogAnchorEl}
            onClose={handleVariableDialogClose}
            title="Variable"
            variableData={bodyVariableData}
            variableType="body"
            activeVariableKey={activeVariableKey}
            onChange={handleChange ?? (() => {})}
            onRemoveField={handleRemoveField ?? (() => {})}
            hasMissingSampleText={hasMissingSampleText}
            // disabled={!!createTemplatePayload?.whatsapp_template_id}
            disabled={!canUpdateTemplate(templateStatusDetails?.whatsapp_template_metadata?.meta_whatsapp_template_status_name, createTemplatePayload, whatsappTemplateTypes, draftId)}
          />
        )}
      </>
    );
}

export default WhatsAppMessageBodyPreview;
